"use client";

import { useState } from "react";
import Link from "next/link";
import { ArrowUpRight, ChevronDown, Sparkles } from "lucide-react";
import { FallbackImage } from "@/components/FallbackImage";
import { PinkImageFallback } from "@/components/PinkImageFallback";
import type { CuratedPage } from "@/lib/supabase/types";

const INITIAL_VISIBLE_PAGES = 4;
const VISIBLE_PAGE_STEP = 4;

export function CuratedPagesBlock({ curatedPages }: { curatedPages: CuratedPage[] }) {
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_PAGES);
  const visiblePages = curatedPages.slice(0, visibleCount);
  const hasMore = visibleCount < curatedPages.length;

  return (
    <section className="-mx-4 mt-14 border-y border-border bg-ink px-4 py-12 sm:-mx-6 sm:px-6 lg:py-14">
      <div>
        <div className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.18em] text-brand">
          <span className="h-px w-6 bg-brand" />
          Curated guides
        </div>
        <h3 className="mt-3 max-w-xl font-display text-4xl font-black leading-[0.95] text-foreground sm:text-5xl">
          Popular neighbourhood<br className="hidden sm:block" /> pages
        </h3>
        <p className="mt-3 max-w-xl text-sm leading-6 text-muted-foreground">Hand-picked local collections from the NV editorial team.</p>
      </div>
      <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {visiblePages.length ? visiblePages.map((page) => <CuratedGuideTile key={page.id} page={page} />) : <PromoCard />}
      </div>
      {hasMore ? (
        <div className="mt-8 flex justify-center">
          <button
            type="button"
            onClick={() => setVisibleCount((count) => Math.min(count + VISIBLE_PAGE_STEP, curatedPages.length))}
            className="nv-button-secondary inline-flex min-h-11 items-center gap-2 px-5 py-3 text-xs"
          >
            Show more
            <ChevronDown className="h-4 w-4" />
          </button>
        </div>
      ) : null}
    </section>
  );
}

function PromoCard() {
  return (
    <Link href="/curated" className="group relative flex flex-col overflow-hidden rounded-xl border border-border bg-card transition hover:-translate-y-0.5 hover:border-brand">
      <div className="relative aspect-[16/10] overflow-hidden">
        <PinkImageFallback ariaLabel="Curated guide image unavailable" headline="Curated Local Guides" footer="Curated" className="h-full w-full" />
        <span className="absolute bottom-3 left-3 inline-flex items-center gap-1 rounded-full bg-brand px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.2em] text-white shadow"><Sparkles className="h-3 w-3" /> Curated</span>
      </div>
      <div className="flex flex-1 flex-col p-4">
        <h3 className="font-display text-[17px] font-black leading-snug line-clamp-2">Curated Local Guides</h3>
        <p className="mt-2 line-clamp-2 text-[12px] text-muted-foreground">Hand-picked events, deals and businesses by theme.</p>
        <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
          <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-brand">Open</span>
          <ArrowUpRight className="h-4 w-4 text-brand" />
        </div>
      </div>
    </Link>
  );
}

function CuratedGuideTile({ page }: { page: CuratedPage }) {
  return (
    <Link href={`/curated/${page.slug}`} className="group flex flex-col overflow-hidden rounded-lg border border-border bg-card transition hover:-translate-y-0.5 hover:border-brand">
      <FallbackImage src={page.hero_image_url} alt={page.title} className="aspect-[16/10] w-full object-cover transition duration-500 group-hover:scale-[1.04]" />
      <div className="p-4">
        <div className="text-[10px] font-black uppercase tracking-[0.18em] text-brand">Guide</div>
        <h4 className="mt-1 font-display text-xl font-black leading-tight">{page.title}</h4>
        {page.subtitle ? <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{page.subtitle}</p> : null}
      </div>
    </Link>
  );
}
